/**
 * Keyboard shortcuts written as "Ctrl+Shift+Space", "Alt+D" or "F8" (the
 * format of the dictation shortcut preference). Keys are matched by their
 * physical position, so a shortcut works the same in every keyboard layout
 * and with Alt held on a Mac.
 */
const MODIFIERS = ['Ctrl', 'Alt', 'Shift', 'Meta'] as const;
type Modifier = (typeof MODIFIERS)[number];

/** While a new shortcut is being chosen, key presses are not shortcuts. */
export const choosingShortcut = { active: false };

function parse(shortcut: string): { modifiers: Set<Modifier>; code: string } | null {
  const parts = shortcut.split('+');
  const key = parts.pop();
  if (!key) return null;
  const modifiers = new Set(parts as Modifier[]);
  if ([...modifiers].some((part) => !MODIFIERS.includes(part))) return null;
  const code = /^[A-Z]$/.test(key) ? `Key${key}` : /^[0-9]$/.test(key) ? `Digit${key}` : key === 'Space' || /^F\d{1,2}$/.test(key) ? key : null;
  return code ? { modifiers, code } : null;
}

export function matchesShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const parsed = parse(shortcut);
  if (!parsed || event.repeat || choosingShortcut.active) return false;
  return (
    event.code === parsed.code &&
    event.ctrlKey === parsed.modifiers.has('Ctrl') &&
    event.altKey === parsed.modifiers.has('Alt') &&
    event.shiftKey === parsed.modifiers.has('Shift') &&
    event.metaKey === parsed.modifiers.has('Meta')
  );
}

/** The shortcut a key press makes, or null while only modifiers are held (for choosing a new shortcut). */
export function shortcutFromEvent(event: KeyboardEvent): string | null {
  let key: string | null = null;
  if (/^Key[A-Z]$/.test(event.code)) key = event.code.slice(3);
  else if (/^Digit\d$/.test(event.code)) key = event.code.slice(5);
  else if (event.code === 'Space' || /^F\d{1,2}$/.test(event.code)) key = event.code;
  if (!key) return null;
  const modifiers = [event.ctrlKey && 'Ctrl', event.altKey && 'Alt', event.shiftKey && 'Shift', event.metaKey && 'Meta'].filter(Boolean);
  return [...modifiers, key].join('+');
}

/** As shown to people: "Ctrl+Shift+Space" (Meta shown as Cmd on a Mac). */
export function shortcutLabel(shortcut: string): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return mac ? shortcut.replace('Meta', 'Cmd').replace('Alt', 'Option') : shortcut.replace('Meta', 'Win');
}
